import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import * as moment from 'moment-timezone';
import { StagingService } from '../../component/assigned-trips/staging/staging.service';
import { SharedDataService } from '../../service/shared-service/shared-data.service';
import { sweetAlert } from '../../utils/swal';

@Component({
  selector: 'app-memberlink-request-logs-modal',
  templateUrl: './memberlink-request-logs-modal.component.html',
  styleUrls: ['./memberlink-request-logs-modal.component.css']
})
export class MemberlinkRequestLogsModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() trip: any;
  @Input() preCooperates: any;
  @Output() onCloseModal = new EventEmitter<any>();

  logs: any[] = [];
  isLoading: boolean = false;
  user: any;
  timeZone: string = 'America/New_York';

  constructor(
    private stagingService: StagingService,
    private sharedDataService: SharedDataService
  ) { }

  ngOnInit() {
    this.getUser();
    if (this.trip) {
      this.fetchLogs();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trip && !changes.trip.firstChange && this.trip) {
      this.fetchLogs();
    }
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          if (user.company && user.company.timeZone) {
            this.timeZone = user.company.timeZone;
          } else if (user.timeZone) {
            this.timeZone = user.timeZone;
          }
        }
      });
  }

  fetchLogs() {
    if (!this.trip) return;
    const memberAssignId = this.trip._id || this.trip.id || this.trip.memberAssign || this.trip.tripId;
    if (!memberAssignId) return;

    this.isLoading = true;
    this.logs = [];

    this.stagingService.getRequestLogs(memberAssignId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isLoading = false;
          if (Array.isArray(data)) {
            this.logs = data.sort((a, b) => {
              const tA = new Date(a.createdAt || a.created_at || a.timestamp || 0).getTime();
              const tB = new Date(b.createdAt || b.created_at || b.timestamp || 0).getTime();
              return tB - tA; // latest first
            });
          } else {
            this.logs = [];
          }
        },
        (err) => {
          this.isLoading = false;
          const msg = (err && err.message) ? err.message : (err || 'Failed to fetch request logs');
          sweetAlert('Error', msg, 'error', 'OK');
        }
      );
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).tz(this.timeZone).format('MM/DD/YYYY hh:mm A');
  }

  getRelativeTime(dateStr: string): string {
    if (!dateStr) return '';
    return moment(dateStr).fromNow();
  }

  getActionLabel(action: string, status?: string): string {
    const raw = action || status || 'Activity';
    return raw.replace(/_/g, ' ');
  }

  getActionBadgeClass(action: string, status?: string): string {
    const act = (action || status || '').toLowerCase();
    if (act.includes('confirm') && !act.includes('unconfirm')) {
      return 'bg-green-500 text-white';
    }
    if (act.includes('unconfirm') || act.includes('reject') || act.includes('cancel')) {
      return 'bg-red-500 text-white';
    }
    if (act.includes('suggest')) {
      return 'bg-yellow-500 text-white';
    }
    if (act.includes('create') || act.includes('intake')) {
      return 'bg-main-primary text-white';
    }
    return 'bg-main-dark text-white';
  }

  getFundingSourceName(): string {
    if (!this.trip) return '';
    const coop = this.trip.cooperate;
    if (coop && typeof coop === 'object' && (coop.displayName || coop.name || coop.companyName)) {
      return coop.displayName || coop.name || coop.companyName;
    }
    if (coop && this.preCooperates && this.preCooperates[coop] && this.preCooperates[coop].displayName) {
      return this.preCooperates[coop].displayName;
    }
    if (this.trip.corporate && (this.trip.corporate.displayName || this.trip.corporate.name)) {
      return this.trip.corporate.displayName || this.trip.corporate.name;
    }
    if (coop && !this.isObjectId(coop)) return coop;
    return '';
  }

  isObjectId(val: any): boolean {
    if (!val || typeof val !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(val);
  }

  isStandingOrder(): boolean {
    if (!this.trip) return false;
    return this.trip.status === 'standing' || !!this.trip.isRecurring || !!this.trip.isStandingOrder || !!(this.trip.recurringDays && this.trip.recurringDays.length);
  }

  closeModal() {
    this.onCloseModal.emit();
  }

  ngOnDestroy(): void {}
}
